// src/components/OmniCine/Media/MediaPanel.tsx
//
// Phase M1: Media Panel (NON-authoritative)
// - Browses seeded assets (v1)
// - Select asset -> preview (<video>/<audio>)
// - Emits CustomEvent("timeline:add", { detail: AddDetail })
//
// Hard rule: does NOT dispatch into reducer directly.

import * as React from "react";
import { createPortal } from "react-dom";

import type { MediaAssetV1 } from "@/contracts/media/MediaAssetV1";
import {
  emitTimelineAddEvent,
  type MediaPanelMode,
  type MediaPanelSetModeDetail,
} from "@/contracts/media/MediaPanelEvents";
import { getOmniCineDevAssetCatalogEntry } from "@/demo/omnicineDevAssetCatalog";
import useAudioWaveformModel from "@/components/OmniCine/Docks/audio/useAudioWaveformModel";
import { getSeedMediaAssets } from "@/demo/seedMediaAssets";
import { buildAudioDockModel } from "@/components/OmniCine/Docks/audio/AudioDockAdapter";
import AudioDockFeature from "@/components/OmniCine/Docks/audio/AudioDockFeature";
import { readClipMediaUrl, readTimelineClips, toDockKind } from "@/components/OmniCine/Docks/shared/mediaDock.helpers";
import MediaDockShell from "@/components/OmniCine/Docks/shared/MediaDockShell";
import useDockPlaybackState from "@/components/OmniCine/Docks/shared/useDockPlaybackState";
import useMediaPreviewSync from "@/components/OmniCine/Docks/shared/useMediaPreviewSync";
import { buildVideoDockModel } from "@/components/OmniCine/Docks/video/VideoDockAdapter";
import VideoDockFeature from "@/components/OmniCine/Docks/video/VideoDockFeature";
import MediaGlassControls from "@/components/OmniCine/media/MediaGlassControls";
import { resolveMediaIdentityTitle } from "@/components/OmniCine/media/resolveMediaTitle";
import { useOmniCineTimeline } from "@/state/useOmniCineTimeline";

type MediaPanelProps = {
  className?: string;
  mode: MediaPanelMode;
  isPinned: boolean;
  focusIntent?: MediaPanelSetModeDetail | null;
  onRequestMode: (
    nextMode: MediaPanelMode,
    nextPinned?: boolean,
    options?: { preserveWitnessBar?: boolean },
  ) => void;
};

const PANEL_STAGE_HEIGHT_PX = 206;
const PANEL_BODY_HEIGHT_PX = 282;
const AUDIO_STAGE_HEIGHT_PX = 150;
const AUDIO_BODY_HEIGHT_PX = 228;
const FOCUS_DOCK_CLEARANCE_PX = 96;
const AUDIO_WAVEFORM_BAR_COUNT = 96;

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

export default function MediaPanel({
  className = "",
  mode,
  isPinned,
  focusIntent = null,
  onRequestMode,
}: MediaPanelProps): JSX.Element | null {
  const {
    state,
    selectedClipId,
    mediaFamilies,
    mediaMembers,
    editorialClipBindingByClipId,
    clipMediaMetadataByClipId,
  } = useOmniCineTimeline();
  const panelMode = mode;
  const isPinnedRef = React.useRef(false);
  React.useEffect(() => {
    isPinnedRef.current = isPinned;
  }, [isPinned]);
  const [query, setQuery] = React.useState("");
  const [selectedId, setSelectedId] = React.useState<string>("");
  const [videoOverlayVisible, setVideoOverlayVisible] = React.useState(true);
  const [videoDurationSec, setVideoDurationSec] = React.useState<number | null>(null);

  const [focusMedia, setFocusMedia] = React.useState<
    | {
        clipId?: string;
        trackId?: string;
        kind: "video" | "audio" | "image" | "unknown";
        mediaId?: string;
        mediaUrl?: string;
        label?: string;
        source?: "import" | "inlineWitness" | "system" | "mediaPanel";
      }
    | null
  >(null);

  const [externalPreview, setExternalPreview] = React.useState<
    | { url: string; kind: "video" | "audio" | "image" | "unknown"; label?: string }
    | null
  >(null);

  const assets = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const seedMediaAssets = getSeedMediaAssets();
    if (!q) return seedMediaAssets;
    return seedMediaAssets.filter((a) => {
      const hay = [a.label, a.id, ...(a.tags ?? [])].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [query]);

  const selectedAsset: MediaAssetV1 | null = React.useMemo(() => {
    return getSeedMediaAssets().find((a) => a.id === selectedId) ?? null;
  }, [selectedId]);

  const focusedClip = React.useMemo(() => {
    const clipId = focusMedia?.clipId ?? focusIntent?.clipId ?? selectedClipId;
    if (!clipId) return null;

    const clips = readTimelineClips(state);
    return clips.find((clip) => String(clip?.id ?? clip?.clipId ?? "") === String(clipId)) ?? null;
  }, [focusIntent?.clipId, focusMedia?.clipId, selectedClipId, state]);

  const focusedClipLabel = React.useMemo(() => {
    if (!focusedClip) return null;
    return (
      (typeof focusedClip?.title === "string" && focusedClip.title) ||
      (typeof focusedClip?.label === "string" && focusedClip.label) ||
      null
    );
  }, [focusedClip]);

  const focusedClipMediaId = React.useMemo(() => {
    if (!focusedClip) return null;
    return (
      (typeof focusedClip?.mediaId === "string" && focusedClip.mediaId) ||
      (typeof focusedClip?.media?.id === "string" && focusedClip.media.id) ||
      null
    );
  }, [focusedClip]);

  const focusedClipMediaUrl = React.useMemo(() => readClipMediaUrl(focusedClip), [focusedClip]);

  const focusedClipKind = React.useMemo(() => toDockKind(focusedClip?.kind), [focusedClip?.kind]);
  const resolvedFocusPreviewUrl = React.useMemo(
    () =>
      externalPreview?.url ??
      focusMedia?.mediaUrl ??
      focusIntent?.mediaUrl ??
      focusedClipMediaUrl ??
      null,
    [externalPreview?.url, focusIntent?.mediaUrl, focusMedia?.mediaUrl, focusedClipMediaUrl],
  );

  const { previewUrl, previewStatus, err } = useMediaPreviewSync({
    panelMode,
    selectedAsset,
    resolvedFocusPreviewUrl,
  });

  React.useEffect(() => {
    setVideoOverlayVisible(true);
    setVideoDurationSec(null);
  }, [previewUrl]);

  React.useEffect(() => {
    if (!focusIntent) {
      setFocusMedia(null);
      setExternalPreview(null);
      return;
    }

    if (focusIntent.mode === "closed") {
      setFocusMedia(null);
      setExternalPreview(null);
      return;
    }

    if (focusIntent.mode === "ingest") {
      setFocusMedia(null);
      return;
    }

    const nextKind = focusIntent.kind ?? "unknown";
    const nextLabel = focusIntent.label ?? focusIntent.clipLabel ?? focusIntent.clipId ?? "Media Focus";
    const nextMediaUrl = focusIntent.mediaUrl;
    const nextMediaId = focusIntent.mediaId;

    setFocusMedia({
      clipId: focusIntent.clipId,
      trackId: focusIntent.trackId,
      kind: nextKind,
      mediaId: nextMediaId,
      mediaUrl: nextMediaUrl,
      label: nextLabel,
      source: focusIntent.source,
    });

    if (nextMediaId) {
      const found = getSeedMediaAssets().find((a) => a.id === nextMediaId);
      if (found) setSelectedId(found.id);
    }

    if (nextMediaUrl) {
      setExternalPreview({
        url: nextMediaUrl,
        kind: nextKind,
        label: nextLabel,
      });
    } else {
      setExternalPreview(null);
    }
  }, [focusIntent]);

  const canAdd = !!selectedAsset;

  const dockToFocus = React.useCallback(() => {
    onRequestMode("focus", false);
  }, [onRequestMode]);

  const openIngestMode = React.useCallback(() => {
    onRequestMode("ingest", false);
    setFocusMedia(null);
  }, [onRequestMode]);

  const onClosePanel = React.useCallback(() => {
    onRequestMode("closed", false, {
      preserveWitnessBar: panelMode === "focus" || panelMode === "overlay",
    });
    setFocusMedia(null);
    setExternalPreview(null);
  }, [onRequestMode, panelMode]);

  React.useEffect(() => {
    if (panelMode !== "overlay") return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (isPinnedRef.current) return;
        e.preventDefault();
        dockToFocus();
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dockToFocus, panelMode]);

  const onAddSelected = React.useCallback(() => {
    openIngestMode();
    if (!selectedAsset) return;

    const st: any = state;

    const start0 =
      typeof st?.playheadSec === "number"
        ? st.playheadSec
        : typeof st?.cursorSec === "number"
          ? st.cursorSec
          : 0;

    const durRaw = Number((selectedAsset as any).durSec ?? 3);
    const durSec = Number.isFinite(durRaw) && durRaw > 0 ? durRaw : 3;

    const startSec = Math.max(0, start0);
    const endSec = startSec + durSec;

    const preferredTrackId = selectedAsset.kind === "audio" ? "a1" : "v1";
    const catalogEntry = getOmniCineDevAssetCatalogEntry(selectedAsset.id);
    const resolvedUrl =
      selectedAsset.ref.provider === "static"
        ? selectedAsset.ref.path
        : selectedAsset.ref.provider === "url"
          ? selectedAsset.ref.url
          : catalogEntry?.mediaUrl;

    emitTimelineAddEvent({
      trackId: preferredTrackId,
      kind: selectedAsset.kind,
      startSec,
      endSec,
      durSec,
      mediaId: selectedAsset.id,
      label: selectedAsset.label,
      sourceAssetId: selectedAsset.id,
      url: resolvedUrl,
      mediaUrl: resolvedUrl,
      previewUrl: resolvedUrl,
      ...(catalogEntry?.ingestMetadata ?? {}),
      source: "user",
    });
  }, [openIngestMode, selectedAsset, state]);

  const previewKind =
    externalPreview?.kind ??
    focusMedia?.kind ??
    focusedClipKind ??
    (selectedAsset?.kind as "video" | "audio" | "image" | "unknown" | undefined) ??
    "unknown";

  const previewLabel =
    focusMedia?.label ??
    focusIntent?.clipLabel ??
    focusIntent?.label ??
    focusedClipLabel ??
    externalPreview?.label ??
    selectedAsset?.label ??
    "Media Preview";
  const readableTitle = React.useMemo(
    () =>
      resolveMediaIdentityTitle({
        clipTitle: focusMedia?.label ?? focusIntent?.clipLabel ?? focusIntent?.label ?? focusedClipLabel,
        assetDisplayName: selectedAsset?.label ?? focusedClipLabel,
        importedLabel: previewLabel,
        mediaUrl: previewUrl ?? focusedClipMediaUrl,
        mediaId: focusMedia?.mediaId ?? focusedClipMediaId ?? selectedAsset?.id,
        kind: previewKind,
      }),
    [focusIntent?.clipLabel, focusIntent?.label, focusMedia?.label, focusMedia?.mediaId, focusedClipLabel, focusedClipMediaId, focusedClipMediaUrl, previewKind, previewLabel, previewUrl, selectedAsset?.id, selectedAsset?.label],
  );
  const kindChip = previewKind === "audio" ? "AUDIO" : previewKind === "video" ? "VIDEO" : previewKind === "image" ? "IMAGE" : "MEDIA";
  const isEmptyFocusState =
    (panelMode === "focus" || panelMode === "overlay") &&
    !focusIntent &&
    !focusMedia &&
    !externalPreview &&
    !selectedClipId &&
    !previewUrl;

  function fmtTime(sec: number | null): string {
    if (typeof sec !== "number" || !Number.isFinite(sec) || sec < 0) return "0:00";
    const total = Math.floor(sec);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }

  const {
    previewVideoRef,
    previewAudioRef,
    mediaDurationSec,
    mediaCurrentTimeSec,
    mediaIsPlaying,
    mediaMuted,
    syncActiveMediaState,
    onGlassPlayPause,
    onGlassMuteToggle,
    onGlassSeek,
  } = useDockPlaybackState({
    panelMode,
    previewKind,
    previewUrl,
  });

  const audioProgressPct =
    mediaDurationSec > 0
      ? Math.min(100, (mediaCurrentTimeSec / mediaDurationSec) * 100)
      : 0;

  const onOverlayTogglePlay = React.useCallback(() => {
    const el = previewVideoRef.current;
    if (!el) return;
    try {
      if (el.paused) {
        const maybe = el.play();
        if (maybe && typeof (maybe as Promise<void>).catch === "function") {
          (maybe as Promise<void>).catch(() => {
            // ignore
          });
        }
        setVideoOverlayVisible(false);
      } else {
        el.pause();
        setVideoOverlayVisible(true);
      }
    } catch {
      // ignore
    }
  }, []);

  const { waveformBars, waveMotionPhase } = useAudioWaveformModel({
    previewKind,
    previewUrl,
    mediaIsPlaying,
    panelMode,
    totalBars: AUDIO_WAVEFORM_BAR_COUNT,
  });

  const audioDockModel = React.useMemo(
    () =>
      previewKind === "audio"
        ? buildAudioDockModel({
            state,
            selectedClipId,
            mediaFamilies,
            mediaMembers,
            editorialClipBindingByClipId,
            clipMediaMetadataByClipId,
            focusIntent,
            focusMedia,
            externalPreview,
            selectedAsset,
            previewStatus,
            previewUrl,
            err,
            mediaCurrentTimeSec,
            mediaDurationSec,
            audioProgressPct,
            waveformBars,
            waveMotionPhase,
            mediaIsPlaying,
            stageHeight: AUDIO_STAGE_HEIGHT_PX,
            previewAudioRef,
            fmtTime,
            syncActiveMediaState,
          })
        : null,
    [audioProgressPct, clipMediaMetadataByClipId, editorialClipBindingByClipId, err, externalPreview, focusIntent, focusMedia, fmtTime, mediaCurrentTimeSec, mediaDurationSec, mediaFamilies, mediaIsPlaying, mediaMembers, previewKind, previewStatus, previewUrl, selectedAsset, selectedClipId, state, syncActiveMediaState, waveformBars, waveMotionPhase],
  );

  const videoDockModel = React.useMemo(
    () =>
      previewKind === "video"
        ? buildVideoDockModel({
            state,
            selectedClipId,
            mediaFamilies,
            mediaMembers,
            editorialClipBindingByClipId,
            clipMediaMetadataByClipId,
            focusIntent,
            focusMedia,
            externalPreview,
            selectedAsset,
            previewStatus,
            previewUrl,
            previewLabel,
            err,
            mediaIsPlaying,
            stageHeight: PANEL_STAGE_HEIGHT_PX,
            previewVideoRef,
            onGlassPlayPause,
            syncActiveMediaState,
          })
        : null,
    [clipMediaMetadataByClipId, editorialClipBindingByClipId, err, externalPreview, focusIntent, focusMedia, mediaFamilies, mediaIsPlaying, mediaMembers, onGlassPlayPause, previewKind, previewLabel, previewStatus, previewUrl, selectedAsset, selectedClipId, state, syncActiveMediaState],
  );

  const renderPreviewSurface = (heightClass = "h-[220px]") => {
    return (
      <div className={cx(heightClass, "rounded-2xl border border-white/10 bg-black/30 overflow-hidden flex items-center justify-center")}>
        {previewStatus === "loading" && <div className="text-sm opacity-70">Resolving URL...</div>}

        {previewStatus === "error" && (
          <div className="px-3 text-sm text-red-200">
            <div className="font-medium">Preview error</div>
            <div className="opacity-80 mt-1">{err}</div>
          </div>
        )}

        {previewStatus === "ready" && previewUrl && previewKind === "audio" && (
          <audio controls preload="metadata" src={previewUrl} className="w-full px-3" />
        )}

        {previewStatus === "ready" && previewUrl && previewKind === "video" && (
          <div className="relative w-full h-full">
            <video
              ref={previewVideoRef}
              controls
              preload="metadata"
              src={previewUrl}
              className="w-full h-full object-contain"
              onPlay={() => setVideoOverlayVisible(false)}
              onPause={() => setVideoOverlayVisible(true)}
              onEnded={() => setVideoOverlayVisible(true)}
              onLoadedMetadata={(e) => setVideoDurationSec(e.currentTarget.duration)}
            />

            <div className="pointer-events-none absolute bottom-12 right-3 rounded-md border border-cyan-300/35 bg-black/55 px-2 py-1 text-xs text-cyan-100">
              len {fmtTime(videoDurationSec)}
            </div>

            <button
              type="button"
              aria-label="Toggle play"
              onClick={onOverlayTogglePlay}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-16 w-16 rounded-full border border-cyan-300/80 bg-cyan-400/20 text-cyan-100 backdrop-blur-sm transition-opacity duration-300"
              style={{
                opacity: videoOverlayVisible ? 1 : 0,
                boxShadow: "0 0 14px rgba(34,211,238,0.7), 0 0 28px rgba(34,211,238,0.45), inset 0 0 10px rgba(34,211,238,0.25)",
              }}
            >
              <span className="block text-2xl leading-none pl-0.5">▶</span>
            </button>
          </div>
        )}

        {previewStatus === "ready" && previewUrl && previewKind === "image" && (
          // eslint-disable-next-line @next/next/no-img-element
          <img alt={previewLabel} src={previewUrl} className="w-full h-full object-contain" />
        )}

        {previewStatus === "ready" && previewUrl && previewKind === "unknown" && <div className="text-sm opacity-70">Preview ready</div>}

        {previewStatus === "idle" && <div className="text-sm opacity-70">No media selected</div>}
      </div>
    );
  };

  const renderIngestPanel = () => (
    <div
      className={cx(
        "pointer-events-auto",
        "w-[720px] max-w-[92vw]",
        "rounded-2xl border border-white/10 bg-black/35 backdrop-blur-md",
        "shadow-[0_12px_40px_rgba(0,0,0,0.35)]",
        "text-white overflow-hidden",
        className,
      )}
      role="region"
      aria-label="Media Panel"
    >
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
        <div className="font-semibold tracking-tight">Media</div>

        <div className="ml-auto flex items-center gap-2">
          <input
            value={query}
            onChange={(e) => {
              onRequestMode("ingest", false);
              setQuery(e.target.value);
            }}
            placeholder="Search assets..."
            className={cx(
              "h-9 w-[260px] max-w-[52vw]",
              "rounded-lg px-3 text-sm bg-white/10 border border-white/10",
              "outline-none focus:ring-2 focus:ring-white/20",
            )}
          />

          <button
            type="button"
            disabled={!canAdd}
            onClick={onAddSelected}
            className={cx(
              "h-9 px-3 rounded-lg text-sm font-medium",
              canAdd ? "bg-white/15 hover:bg-white/20 border border-white/10" : "bg-white/5 border border-white/5 opacity-60 cursor-not-allowed",
            )}
          >
            Add Selected
          </button>

          <button
            type="button"
            onClick={onClosePanel}
            className="h-9 px-3 rounded-lg text-sm font-medium bg-white/8 hover:bg-white/12 border border-white/10"
          >
            Close
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-0">
        <div className="col-span-5 border-r border-white/10 max-h-[240px] overflow-auto">
          {assets.map((a) => {
            const active = a.id === selectedId;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => {
                  onRequestMode("ingest", false);
                  setSelectedId(a.id);
                }}
                className={cx("w-full text-left px-4 py-3", "hover:bg-white/5", active && "bg-white/10")}
              >
                <div className="flex items-center gap-2">
                  <div className="text-xs px-2 py-0.5 rounded-md bg-white/10 border border-white/10">{a.kind}</div>
                  <div className="font-medium text-sm truncate">{a.label}</div>
                </div>
                <div className="text-xs opacity-70 mt-1 truncate">{a.id}</div>
              </button>
            );
          })}

          {!assets.length && <div className="px-4 py-6 text-sm opacity-70">No assets match.</div>}
        </div>

        <div className="col-span-7 px-4 py-3">
          <div className="text-xs opacity-70 mb-2">Preview</div>
          {renderPreviewSurface("h-[180px]")}
          {selectedAsset && (
            <div className="text-xs opacity-70 truncate mt-2">
              {selectedAsset.ref.provider === "registry"
                ? `registry:${selectedAsset.ref.key}`
                : selectedAsset.ref.provider === "firebase"
                  ? `firebase:${selectedAsset.ref.storagePath}`
                  : selectedAsset.ref.provider === "static"
                    ? `static:${selectedAsset.ref.path}`
                    : `url:${selectedAsset.ref.url}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderExpandedPanelCard = (mode: "focus" | "overlay") => {
    const title = isEmptyFocusState
      ? "Focus Panel"
      : previewKind === "audio"
        ? audioDockModel?.title ?? readableTitle
        : previewKind === "video"
          ? videoDockModel?.title ?? readableTitle
          : readableTitle;
    const subtitle = isEmptyFocusState
      ? "Awaiting media selection"
      : previewKind === "audio"
        ? audioDockModel?.subtitle ?? "Audio Focus"
        : previewKind === "video"
          ? videoDockModel?.subtitle ?? "Video Focus"
          : "Media Focus";
    const resolvedKindChip = isEmptyFocusState
      ? "READY"
      : previewKind === "audio"
        ? audioDockModel?.kindChip ?? kindChip
        : previewKind === "video"
          ? videoDockModel?.kindChip ?? kindChip
          : kindChip;
    const sourceChip = isEmptyFocusState
      ? null
      : previewKind === "audio"
        ? audioDockModel?.sourceChip ?? null
        : previewKind === "video"
          ? videoDockModel?.sourceChip ?? null
          : focusMedia?.source
      ? focusMedia.source === "inlineWitness"
        ? "Inline Witness"
        : focusMedia.source === "import"
          ? "Import"
          : focusMedia.source === "mediaPanel"
            ? "Media Panel"
            : "System"
      : null;

    const controls =
      isEmptyFocusState ? null : previewKind === "audio" || previewKind === "video" ? (
        <div className="rounded-lg border border-white/10 bg-black/30 px-1.5 py-1">
          {previewStatus === "ready" && previewUrl ? (
            <MediaGlassControls
              kind={previewKind === "video" ? "video" : "audio"}
              isPlaying={mediaIsPlaying}
              currentTime={mediaCurrentTimeSec}
              duration={mediaDurationSec}
              muted={mediaMuted}
              showOverlayAction={false}
              onPlayPause={onGlassPlayPause}
              onMuteToggle={onGlassMuteToggle}
              onSeek={onGlassSeek}
            />
          ) : previewStatus === "loading" ? (
            <div className="px-3 py-2 text-sm opacity-70">Resolving URL...</div>
          ) : previewStatus === "error" ? (
            <div className="px-3 py-2 text-sm text-red-200">{err}</div>
          ) : (
            <div className="px-3 py-2 text-sm opacity-70">No media selected</div>
          )}
        </div>
      ) : null;

    let body: React.ReactNode;
    if (isEmptyFocusState) {
      body = (
        <div
          className="rounded-xl border border-dashed border-cyan-200/18 bg-[linear-gradient(180deg,rgba(8,28,36,0.22)_0%,rgba(5,10,18,0.38)_100%)] px-5 py-6 text-sm text-white/72 shadow-[inset_0_1px_0_rgba(160,240,255,0.06)]"
          style={{ minHeight: 120 }}
        >
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-100/78">Ready</div>
          <div className="mt-2 text-sm text-white/84">No media selected.</div>
          <div className="mt-1 text-xs text-white/56">Choose another clip from the timeline or inline witness rail to continue.</div>
        </div>
      );
    } else if (previewKind === "audio") {
      body = audioDockModel ? <AudioDockFeature {...audioDockModel.featureProps} /> : null;
    } else if (previewKind === "video") {
      body = videoDockModel ? <VideoDockFeature {...videoDockModel.featureProps} /> : null;
    } else {
      body = (
        <div
          className="rounded-xl border border-cyan-200/22 bg-[linear-gradient(180deg,rgba(8,28,36,0.32)_0%,rgba(5,10,18,0.55)_100%)] overflow-hidden flex items-center justify-center shadow-[inset_0_1px_0_rgba(160,240,255,0.08)]"
          style={{ height: PANEL_STAGE_HEIGHT_PX }}
        >
          {previewStatus === "loading" && <div className="text-sm opacity-70">Resolving URL...</div>}

          {previewStatus === "error" && (
            <div className="px-3 text-sm text-red-200">
              <div className="font-medium">Preview error</div>
              <div className="opacity-80 mt-1">{err}</div>
            </div>
          )}

          {previewStatus === "ready" && previewUrl && previewKind === "image" && (
            <img alt={previewLabel} src={previewUrl} className="w-full h-full object-contain" />
          )}

          {previewStatus === "ready" && previewUrl && previewKind === "unknown" && (
            <div className="text-sm opacity-70">Preview ready, but media kind is unknown.</div>
          )}

          {!previewUrl && <div className="text-sm opacity-70">No media selected</div>}
        </div>
      );
    }

    return (
      <MediaDockShell
        className={className}
        mode={mode}
        title={title}
        subtitle={subtitle}
        kindChip={resolvedKindChip}
        sourceChip={sourceChip}
        isPinned={isPinned}
        onTogglePin={() => onRequestMode(mode, !isPinned)}
        onClose={onClosePanel}
        bodyHeight={previewKind === "audio" ? AUDIO_BODY_HEIGHT_PX : PANEL_BODY_HEIGHT_PX}
        body={body}
        controls={controls}
      />
    );
  };

  if (panelMode === "closed") return null;

  if (panelMode === "focus" || panelMode === "overlay") {
    const dock =
      typeof document !== "undefined"
        ? (document.querySelector("[data-oc-media-panel-focus-dock]") as HTMLElement | null)
        : null;
    if (!dock) return null;

    const dockNode = (
      <div
        className="pointer-events-auto absolute right-0"
        role="region"
        aria-label={panelMode === "overlay" ? "Media Overlay Panel" : "Media Focus Panel"}
        style={{ bottom: FOCUS_DOCK_CLEARANCE_PX }}
      >
        {renderExpandedPanelCard(panelMode)}
      </div>
    );

    return createPortal(dockNode, dock);
  }

  if (panelMode === "ingest") {
    const dock =
      typeof document !== "undefined"
        ? (document.querySelector("[data-oc-media-panel-ingest-dock]") as HTMLElement | null)
        : null;

    if (!dock) return renderIngestPanel();

    const dockNode = (
      <div className="pointer-events-auto absolute left-0 top-0" role="region" aria-label="Media Ingest Panel">
        {renderIngestPanel()}
      </div>
    );

    return createPortal(dockNode, dock);
  }

  return renderIngestPanel();
}
